import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkle, TrendUp, ArrowRight, FilePdf } from "@phosphor-icons/react";
import { getStatsSummary } from "../lib/api";

// Native-SVG sparkline (60×20) — cheap, dependency-free, dense info.
const Sparkline = ({ points = [], color = "#6043EF" }) => {
  if (!points || points.length < 2) return null;
  const w = 60, h = 20;
  const min = Math.min(...points), max = Math.max(...points);
  const range = max - min || 1;
  const step = w / (points.length - 1);
  const path = points.map((v, i) =>
    `${i === 0 ? "M" : "L"}${(i * step).toFixed(1)},${(h - ((v - min) / range) * h).toFixed(1)}`,
  ).join(" ");
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
      <path d={path} stroke={color} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={((points.length - 1) * step).toFixed(1)}
        cy={(h - ((points[points.length - 1] - min) / range) * h).toFixed(1)}
        r="2" fill={color} />
    </svg>
  );
};

// Two-card informational rail rendered just below the Live Counselling Board on the
// landing page. Cards deep-link into /stats with the right tab preselected. The
// entire component auto-hides if `college_yearly_stats` has zero entries — so the
// homepage stays clean off-season.
export const CollegeStatsRail = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    getStatsSummary()
      .then((r) => setSummary(r.data))
      .catch(() => setSummary(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (!summary || (summary.new_colleges === 0 && summary.colleges_with_increase === 0)) return null;

  const y = summary.year;
  const ayLabel = `AY ${y}-${String(y + 1).slice(-2)}`;
  // Pub date arrives as "DD.MM.YYYY" or "DD/MM/YYYY" from the NMC PDF footer.
  // Reformat to "DD Mon YYYY" (e.g. "13 Jul 2026") + Date obj for freshness.
  const parsePubDate = (s) => {
    if (!s) return { label: null, date: null };
    const m = String(s).match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{2,4})$/);
    if (!m) return { label: s, date: null };
    const [_, d, mo, yr] = m;
    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    const idx = parseInt(mo, 10) - 1;
    const yyyy = yr.length === 2 ? `20${yr}` : yr;
    const label = `${d.padStart(2, "0")} ${months[idx] || mo} ${yyyy}`;
    const date = new Date(parseInt(yyyy, 10), idx, parseInt(d, 10));
    return { label, date: isNaN(date) ? null : date };
  };
  const { label: pubDateStr, date: pubDate } = parsePubDate(summary.pub_date);
  // Show blinking "NEW" badge for 7 days after publish date.
  const isFresh = pubDate && (Date.now() - pubDate.getTime()) < 7 * 24 * 60 * 60 * 1000;

  return (
    <section className="border-b border-zinc-200 py-10 bg-white" data-testid="college-stats-rail">
      {/* Width matches CentralNewsroom's max-w-7xl container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF2A00]">
              WHAT&apos;S NEW · {ayLabel}
            </p>
            <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-[#09090B] font-heading mt-1 flex items-center flex-wrap gap-x-2 gap-y-1" data-testid="stats-rail-heading">
              <span>NMC : Seat Matrix Stats</span>
              {pubDateStr && (
                <span className="text-sm font-mono font-normal text-zinc-500 inline-flex items-center gap-1.5" data-testid="stats-rail-pubdate">
                  <span>· Published — {pubDateStr}</span>
                  {isFresh && (
                    <span
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#FF2A00] text-white text-[9px] font-bold uppercase tracking-[0.15em] animate-pulse"
                      data-testid="stats-rail-new-badge"
                      title="Published within the last 7 days"
                    >
                      <span className="w-1 h-1 rounded-full bg-white animate-ping" />
                      NEW
                    </span>
                  )}
                  {summary.source_url && (
                    <a
                      href={summary.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-[0.15em] text-[#6043EF] hover:underline"
                      data-testid="stats-rail-source-link"
                      onClick={(e) => e.stopPropagation()}
                      title="Open NMC seat matrix PDF"
                    >
                      <FilePdf size={12} weight="fill" /> Source PDF
                    </a>
                  )}
                </span>
              )}
            </h2>
          </div>
          <button onClick={() => navigate("/stats")}
            className="hidden sm:inline-flex items-center gap-1 text-xs font-bold uppercase tracking-[0.15em] text-[#6043EF] hover:underline"
            data-testid="stats-rail-viewall">
            View all stats <ArrowRight size={12} weight="bold" />
          </button>
        </div>

        {/*
          Netflix-style horizontally scrollable rail. Fixed card widths so multiple
          cards can be appended later without breaking layout. Horizontal padding is
          bled with a negative margin so first/last cards align with section gutter
          while still allowing edge-to-edge scroll on mobile.
        */}
        <div
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-3 -mx-4 sm:-mx-6 px-4 sm:px-6 scroll-smooth [scrollbar-width:thin]"
          data-testid="stats-rail-track"
        >
          {/* Card 1 — New colleges */}
          <button onClick={() => navigate("/stats?tab=new")} data-testid="rail-card-new-colleges"
            className="group snap-start shrink-0 w-[280px] sm:w-[340px] md:w-[380px] text-left border-2 border-zinc-200 hover:border-[#FF2A00] hover:shadow-[4px_4px_0px_0px_#FF2A00] transition-all p-5 bg-gradient-to-br from-white to-orange-50/40">
            <div className="flex items-start justify-between mb-3">
              <div className="w-10 h-10 bg-[#FF2A00] text-white flex items-center justify-center">
                <Sparkle size={20} weight="fill" />
              </div>
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">NEW ESTABLISHMENTS</span>
            </div>
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-4xl font-bold text-[#09090B] font-heading">{summary.new_colleges}</span>
              <span className="text-sm text-zinc-500">new medical colleges</span>
            </div>
            <p className="text-sm text-zinc-600 mb-3">
              Approved by NMC for {ayLabel} — adding{" "}
              <span className="font-bold text-[#FF2A00]">{summary.new_seats?.toLocaleString("en-IN")} MBBS seats</span> to the national capacity.
            </p>
            <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-[0.15em] text-[#FF2A00] group-hover:gap-2 transition-all">
              See the list <ArrowRight size={12} weight="bold" />
            </span>
          </button>

          {/* Card 2 — Seat increases */}
          <button onClick={() => navigate("/stats?tab=increase")} data-testid="rail-card-seat-increase"
            className="group snap-start shrink-0 w-[280px] sm:w-[340px] md:w-[380px] text-left border-2 border-zinc-200 hover:border-[#6043EF] hover:shadow-[4px_4px_0px_0px_#6043EF] transition-all p-5 bg-gradient-to-br from-white to-violet-50/40">
            <div className="flex items-start justify-between mb-3">
              <div className="w-10 h-10 bg-[#6043EF] text-white flex items-center justify-center">
                <TrendUp size={20} weight="fill" />
              </div>
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">SEAT INTAKE INCREASED</span>
            </div>
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-4xl font-bold text-[#09090B] font-heading">{summary.colleges_with_increase}</span>
              <span className="text-sm text-zinc-500">colleges expanded</span>
            </div>
            <p className="text-sm text-zinc-600 mb-3">
              Existing MBBS colleges added{" "}
              <span className="font-bold text-[#6043EF]">+{summary.seats_added_by_increase?.toLocaleString("en-IN")} seats</span> this year — same {ayLabel} cycle.
            </p>
            <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-[0.15em] text-[#6043EF] group-hover:gap-2 transition-all">
              See who grew <ArrowRight size={12} weight="bold" />
            </span>
          </button>
        </div>
      </div>
    </section>
  );
};

export default CollegeStatsRail;
export { Sparkline };
